"use client";

import * as React from "react";
import {
  format,
  getMonth,
  getYear,
  addMonths,
  subMonths,
  startOfDay,
} from "date-fns";
import { srLatn } from "date-fns/locale";
import { cn } from "~/lib/utils";
import { Button } from "./button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TimeSlotPicker } from "../available-time-slots/time-grid";
import { availabilityOptionVariants } from "../available-time-slots/availability-option";

const sr = {
  previousMonth: "Prethodni mesec",
  nextMonth: "Sledeći mesec",
};

type CalendarDay = {
  day: number;
  isCurrentMonth: boolean;
  isPrevMonth: boolean;
  isNextMonth: boolean;
  date: Date;
  hasSlots: boolean;
  isMarked: boolean;
};

const generateCalendarDays = (
  year: number,
  month: number,
  availableTimeslots?: Record<string, Date[]>,
  markedDates?: Date[],
): CalendarDay[] => {
  const firstDay = new Date(year, month, 1).getDay();
  const firstDayAdjusted = firstDay === 0 ? 6 : firstDay - 1;

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysFromPrevMonth = firstDayAdjusted;
  const totalDays = daysFromPrevMonth + daysInMonth;
  const rows = Math.ceil(totalDays / 7);
  const totalCells = rows * 7;

  const days = [];

  const prevMonthDays = new Date(year, month, 0).getDate();
  for (let i = 0; i < daysFromPrevMonth; i++) {
    const dayNum = prevMonthDays - daysFromPrevMonth + i + 1;
    const date = new Date(year, month - 1, dayNum);
    const dateStr = format(date, "yyyy-MM-dd", { locale: srLatn });
    const hasSlots = (availableTimeslots?.[dateStr]?.length ?? 0) > 0;
    days.push({
      day: dayNum,
      isCurrentMonth: false,
      isPrevMonth: true,
      isNextMonth: false,
      date,
      hasSlots,
      isMarked: false,
    });
  }

  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(year, month, i);
    const dateStr = format(date, "yyyy-MM-dd", { locale: srLatn });
    const hasSlots = (availableTimeslots?.[dateStr]?.length ?? 0) > 0;

    const isMarked =
      markedDates?.some(
        (markedDate) =>
          markedDate.getDate() === date.getDate() &&
          markedDate.getMonth() === date.getMonth() &&
          markedDate.getFullYear() === date.getFullYear(),
      ) ?? false;

    days.push({
      day: i,
      isCurrentMonth: true,
      isPrevMonth: false,
      isNextMonth: false,
      date,
      hasSlots,
      isMarked,
    });
  }

  const nextMonthDays = totalCells - (daysFromPrevMonth + daysInMonth);
  for (let i = 1; i <= nextMonthDays; i++) {
    const date = new Date(year, month + 1, i);
    const dateStr = format(date, "yyyy-MM-dd", { locale: srLatn });
    const hasSlots = (availableTimeslots?.[dateStr]?.length ?? 0) > 0;
    days.push({
      day: i,
      isCurrentMonth: false,
      isPrevMonth: false,
      isNextMonth: true,
      date,
      hasSlots,
      isMarked: false,
    });
  }

  return days;
};

type CalendarProps = {
  selected?: Date;
  onSelect?: (date: Date) => void;
  className?: string;
  density?: "default" | "compact";
  fromDate?: Date;
  toDate?: Date;
  availableTimeslots?: Record<string, Date[]>;
  markedDates?: Date[];
};

export const Calendar = ({
  selected,
  onSelect,
  className,
  density = "default",
  fromDate,
  toDate,
  availableTimeslots = {},
  markedDates = [],
}: CalendarProps) => {
  const [viewDate, setViewDate] = React.useState(selected || new Date());

  React.useEffect(() => {
    if (selected) {
      setViewDate(selected);
    }
  }, [selected]);

  const year = getYear(viewDate);
  const month = getMonth(viewDate);
  const days = generateCalendarDays(
    year,
    month,
    availableTimeslots,
    markedDates,
  );

  const handlePrevMonth = () => {
    setViewDate((prevDate) => subMonths(prevDate, 1));
  };

  const handleNextMonth = () => {
    setViewDate((prevDate) => addMonths(prevDate, 1));
  };

  const handleSelectDate = (day: CalendarDay) => {
    const dayStart = startOfDay(day.date);
    if (
      (fromDate && dayStart < startOfDay(fromDate)) ||
      (toDate && dayStart > startOfDay(toDate))
    ) {
      return;
    }
    if (day.isPrevMonth) {
      setViewDate((prev) => subMonths(prev, 1));
    } else if (day.isNextMonth) {
      setViewDate((prev) => addMonths(prev, 1));
    }
    onSelect?.(day.date);
  };

  const isSelectedDate = (dateToCheck: Date) => {
    if (!selected) return false;
    return (
      dateToCheck.getDate() === selected.getDate() &&
      dateToCheck.getMonth() === selected.getMonth() &&
      dateToCheck.getFullYear() === selected.getFullYear()
    );
  };

  const isDateDisabled = (dateToCheck: Date) => {
    const dayStart = startOfDay(dateToCheck);
    if (fromDate && dayStart < startOfDay(fromDate)) return true;
    if (toDate && dayStart > startOfDay(toDate)) return true;

    const dateStr = format(dateToCheck, "yyyy-MM-dd", { locale: srLatn });
    const hasSlots = (availableTimeslots?.[dateStr]?.length ?? 0) > 0;
    return !hasSlots;
  };

  return (
    <div className={cn("w-full", className)}>
      <div
        className={cn(
          "mb-1 flex items-center justify-between pt-2",
          density === "compact" ? "px-3" : "px-4",
        )}
      >
        <h2
          className={cn(
            "font-semibold capitalize",
            density === "compact" ? "text-base" : "text-xl",
          )}
          aria-live="polite"
        >
          {format(viewDate, "MMMM", { locale: srLatn })}{" "}
          <span className="text-muted-foreground">
            {format(viewDate, "yyyy", { locale: srLatn })}
          </span>
        </h2>
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrevMonth}
            disabled={
              fromDate
                ? subMonths(viewDate, 1).getMonth() < fromDate.getMonth() &&
                  subMonths(viewDate, 1).getFullYear() <= fromDate.getFullYear()
                : false
            }
            className="h-7 w-7 text-muted-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">{sr.previousMonth}</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNextMonth}
            disabled={
              toDate
                ? addMonths(viewDate, 1).getMonth() > toDate.getMonth() &&
                  addMonths(viewDate, 1).getFullYear() >= toDate.getFullYear()
                : false
            }
            className="h-7 w-7 text-muted-foreground"
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">{sr.nextMonth}</span>
          </Button>
        </div>
      </div>

      <div
        className={cn(
          "border-b border-t py-2",
          density === "compact" ? "mb-1" : "mb-2 md:mb-6",
        )}
      >
        <div className="grid grid-cols-7 text-center">
          {["PON", "UTO", "SRE", "ČET", "PET", "SUB", "NED"].map((day) => (
            <div
              key={day}
              aria-hidden="true"
              className="text-xs font-medium text-muted-foreground"
            >
              {day}
            </div>
          ))}
        </div>
      </div>

      <div
        className={cn(
          "grid grid-cols-7 gap-1 p-1",
          density === "default" && "md:gap-2 md:p-2",
        )}
      >
        {days.map((day, index) => {
          const isSelected = isSelectedDate(day.date);
          const isOtherMonthWithSlots =
            (day.isPrevMonth || day.isNextMonth) && day.hasSlots;
          const isDisabled =
            isDateDisabled(day.date) ||
            (!day.isCurrentMonth && !isOtherMonthWithSlots);

          const dayLabel = `${format(day.date, "d. MMMM yyyy.", { locale: srLatn })}${
            day.hasSlots
              ? ", ima slobodnih termina"
              : ", nema slobodnih termina"
          }${isSelected ? ", izabrano" : ""}`;

          return (
            <button
              key={`${day.day}-${index}`}
              data-testid={`calendar-day-${day.day}-${day.isCurrentMonth ? "current" : "other"}`}
              aria-label={dayLabel}
              className={cn(
                availabilityOptionVariants({
                  state:
                    isSelected && day.hasSlots
                      ? "selected"
                      : day.hasSlots
                        ? "available"
                        : "unavailable",
                }),
                "relative flex aspect-square w-full items-center justify-center text-center font-normal",
                density === "compact"
                  ? "h-8 rounded-md text-xs"
                  : "h-12 rounded-lg text-sm sm:h-12",
                !day.isCurrentMonth &&
                  !isOtherMonthWithSlots &&
                  "text-muted-foreground opacity-50",
                !isSelected && isOtherMonthWithSlots && "opacity-60",
                !isSelected &&
                  !day.hasSlots &&
                  day.isCurrentMonth &&
                  "text-muted-foreground/55",
                (isDisabled || !day.hasSlots) && "pointer-events-none",
              )}
              disabled={isDisabled || !day.hasSlots}
              onClick={() => handleSelectDate(day)}
            >
              <span>{day.day}</span>
              {day.isMarked && (
                <div
                  className={cn(
                    "absolute bottom-1 h-1 w-1 rounded-full bg-brand-action",
                    density === "default" && "md:bottom-2 md:h-1.5 md:w-1.5",
                  )}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

type CustomCalendarProps = {
  mode?: "single" | "range" | "multiple";
  selected?: Date | Date[] | undefined;
  onSelect?: (date: Date | undefined) => void;
  className?: string;
  fromDate?: Date;
  toDate?: Date;
  availableTimeslots?: Record<string, Date[]>;
  markedDates?: Date[];
  noTimeslotsContent?: (date: Date) => React.ReactNode;
  timeslotsFooterContent?: (date: Date) => React.ReactNode;
};

function getFirstSelectedDate(selected: Date | Date[] | undefined) {
  if (!selected) return undefined;
  return Array.isArray(selected) ? selected[0] : selected;
}

export const CustomCalendar = ({
  selected,
  onSelect,
  className,
  fromDate,
  toDate,
  availableTimeslots = {},
  markedDates = [],
  noTimeslotsContent,
  timeslotsFooterContent,
}: CustomCalendarProps) => {
  const [date, setDate] = React.useState<Date | undefined>(
    getFirstSelectedDate(selected),
  );

  React.useEffect(() => {
    const nextSelected = getFirstSelectedDate(selected);
    if (!nextSelected) return;
    setDate(nextSelected);
  }, [selected]);

  const selectedDateStr = date
    ? format(date, "yyyy-MM-dd", { locale: srLatn })
    : "";
  const timeslots = selectedDateStr
    ? availableTimeslots[selectedDateStr] || []
    : [];

  const handleSelectDate = (selectedDate: Date) => {
    setDate(selectedDate);
    if (!timeslots.length) {
      onSelect?.(selectedDate);
    }
  };

  const handleSelectTime = (slot: Date) => {
    onSelect?.(slot);
  };

  return (
    <div className={cn("w-full", className)}>
      <div className="flex flex-col md:flex-row md:gap-8">
        <div className="w-full md:w-auto">
          <Calendar
            selected={date}
            onSelect={handleSelectDate}
            fromDate={fromDate}
            toDate={toDate}
            availableTimeslots={availableTimeslots}
            markedDates={markedDates}
          />
        </div>

        {date && (
          <div className="mt-8 min-h-[300px] w-full md:mt-0 md:min-h-[360px] md:min-w-[200px] md:flex-1">
            <div className="min-h-[244px] md:min-h-[304px]">
              <div className="flex min-h-[244px] flex-col space-y-2 px-2 md:min-h-[304px] md:pr-4">
                {timeslots.length > 0 ? (
                  <>
                    <TimeSlotPicker
                      slots={timeslots}
                      onSlotSelect={handleSelectTime}
                      heading={
                        <>
                          {format(date, "EEEE", { locale: srLatn })}{" "}
                          <span className="font-normal">
                            {format(date, "d", { locale: srLatn })}
                          </span>
                        </>
                      }
                    />
                    {timeslotsFooterContent && (
                      <div className="mt-2 pb-4">
                        {timeslotsFooterContent(date)}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="px-2 md:pr-4">
                    {noTimeslotsContent?.(date) ?? null}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
